import type { CSSProperties } from "react";

import {
  AIRCRAFT_SUPPORT_CASES,
  type ProvenanceMode,
  type SupportCase,
} from "./interpolantProvenanceModel";
import "./InterpolantSupportMap.css";

const PLOT = {
  width: 560,
  height: 420,
  padLeft: 58,
  padRight: 34,
  padTop: 36,
  padBottom: 58,
};

const LATENT_X_VALUES = AIRCRAFT_SUPPORT_CASES.map((supportCase) => supportCase.latent[0]);
const LATENT_Y_VALUES = AIRCRAFT_SUPPORT_CASES.map((supportCase) => supportCase.latent[1]);
const LATENT_X_MIN = Math.min(-1.15, ...LATENT_X_VALUES);
const LATENT_X_MAX = Math.max(1.15, ...LATENT_X_VALUES);
const LATENT_Y_MIN = Math.min(-1.15, ...LATENT_Y_VALUES);
const LATENT_Y_MAX = Math.max(1.15, ...LATENT_Y_VALUES);
const GRID_TICKS = [-1, -0.5, 0, 0.5, 1];

type PlotPoint = { x: number; y: number };

function scaleX(value: number) {
  const span = LATENT_X_MAX - LATENT_X_MIN;
  return PLOT.padLeft + ((value - LATENT_X_MIN) / span) * (PLOT.width - PLOT.padLeft - PLOT.padRight);
}

function scaleY(value: number) {
  const span = LATENT_Y_MAX - LATENT_Y_MIN;
  return PLOT.height - PLOT.padBottom - ((value - LATENT_Y_MIN) / span) * (PLOT.height - PLOT.padTop - PLOT.padBottom);
}

function cross(origin: PlotPoint, a: PlotPoint, b: PlotPoint) {
  return (a.x - origin.x) * (b.y - origin.y) - (a.y - origin.y) * (b.x - origin.x);
}

function convexHull(points: PlotPoint[]) {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const lower: PlotPoint[] = [];
  const upper: PlotPoint[] = [];

  for (const point of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, point) <= 0) lower.pop();
    lower.push(point);
  }

  for (const point of [...sorted].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, point) <= 0) upper.pop();
    upper.push(point);
  }

  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

export function InterpolantSupport2D({
  latent,
  supportCases,
  mode,
}: {
  latent: [number, number, number];
  supportCases: SupportCase[];
  mode: ProvenanceMode;
}) {
  const supportById = new Map(supportCases.map((support) => [support.id, support]));
  const activeColor = mode === "interpolated" ? "#62c39a" : "#e0a458";
  const queryX = scaleX(latent[0]);
  const queryY = scaleY(latent[1]);
  const hullPoints = convexHull(
    AIRCRAFT_SUPPORT_CASES.map((supportCase) => ({
      x: scaleX(supportCase.latent[0]),
      y: scaleY(supportCase.latent[1]),
    })),
  );
  const hullPointString = hullPoints.map((point) => `${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");

  return (
    <div className="ipd-map-frame" data-testid="interpolant-surface-map">
      <svg viewBox={`0 0 ${PLOT.width} ${PLOT.height}`} role="img" aria-label="Aircraft reduced-basis support map">
        <defs>
          <linearGradient id="ipd-envelope-gradient" x1="0%" x2="100%" y1="0%" y2="100%">
            <stop offset="0%" stopColor="#5ec8e0" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#62c39a" stopOpacity="0.08" />
          </linearGradient>
          <filter id="ipd-query-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {GRID_TICKS.map((tick) => (
          <line className={tick === 0 ? "ipd-map-zero" : "ipd-map-grid"} key={`x-${tick}`} x1={scaleX(tick)} x2={scaleX(tick)} y1={PLOT.padTop} y2={PLOT.height - PLOT.padBottom} />
        ))}
        {GRID_TICKS.map((tick) => (
          <line className={tick === 0 ? "ipd-map-zero" : "ipd-map-grid"} key={`y-${tick}`} x1={PLOT.padLeft} x2={PLOT.width - PLOT.padRight} y1={scaleY(tick)} y2={scaleY(tick)} />
        ))}

        <polygon className="ipd-map-hull" points={hullPointString} />
        <polyline className="ipd-map-hull-edge" points={`${hullPointString} ${hullPoints[0]?.x.toFixed(1)},${hullPoints[0]?.y.toFixed(1)}`} />
        <text className="ipd-map-chip" x={PLOT.padLeft + 12} y={PLOT.padTop + 20}>CFD support hull</text>
        <text className="ipd-map-label" x={PLOT.padLeft} y={22}>trim / cg / stability mode</text>
        <text className="ipd-map-label" x={PLOT.width - 190} y={PLOT.height - 18}>lift / propulsor loading mode</text>

        {supportCases.map((support) => {
          const supportCase = AIRCRAFT_SUPPORT_CASES.find((candidate) => candidate.id === support.id);
          if (!supportCase) return null;
          return <line className="ipd-map-link" key={support.id} x1={queryX} y1={queryY} x2={scaleX(supportCase.latent[0])} y2={scaleY(supportCase.latent[1])} strokeWidth={1 + support.weight * 4} />;
        })}

        {AIRCRAFT_SUPPORT_CASES.map((supportCase) => {
          const support = supportById.get(supportCase.id);
          return (
            <g key={supportCase.id}>
              <circle className="ipd-map-sample" cx={scaleX(supportCase.latent[0])} cy={scaleY(supportCase.latent[1])} r={3.2} />
              {support ? (
                <>
                  <circle className="ipd-map-support-ring" cx={scaleX(supportCase.latent[0])} cy={scaleY(supportCase.latent[1])} r={6 + support.weight * 18} strokeWidth={1.3 + support.weight * 4} />
                  <circle className="ipd-map-support-core" cx={scaleX(supportCase.latent[0])} cy={scaleY(supportCase.latent[1])} r={4.2} />
                </>
              ) : null}
            </g>
          );
        })}

        <line className="ipd-map-crosshair" x1={queryX} x2={queryX} y1={PLOT.padTop} y2={PLOT.height - PLOT.padBottom} />
        <line className="ipd-map-crosshair" x1={PLOT.padLeft} x2={PLOT.width - PLOT.padRight} y1={queryY} y2={queryY} />
        <g className="ipd-map-query" filter="url(#ipd-query-glow)" style={{ "--query-color": activeColor } as CSSProperties}>
          <circle cx={queryX} cy={queryY} r={13} />
          <circle cx={queryX} cy={queryY} r={4.4} />
          <line x1={queryX - 22} x2={queryX - 9} y1={queryY} y2={queryY} />
          <line x1={queryX + 9} x2={queryX + 22} y1={queryY} y2={queryY} />
          <line x1={queryX} x2={queryX} y1={queryY - 22} y2={queryY - 9} />
          <line x1={queryX} x2={queryX} y1={queryY + 9} y2={queryY + 22} />
        </g>
      </svg>
    </div>
  );
}
